import React, { useContext, useMemo, useEffect } from 'react';
import { Box, Autocomplete, TextField } from '@mui/material';

// Services
import dictionaryService from '../../../services/dictionaryService.js';

// Context
import ProjectViewContext from '../../../context/ProjectView';

// ---- Helpers for Subjects Autocomplete ----
const normalizeSubjectKey = (s) =>
  String(s ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

export default function SubjectsField({
  shot,
  scene,
  dictionaries,
  updateDictionaries,
  handleShotFieldChange,
  locked = false,
  lockKey,
  id,
  sx,
}) {
  const { lockField, unlockField, setView } = useContext(ProjectViewContext);

  const subjectOptions = useMemo(() => {
    const list = Array.isArray(dictionaries?.subjects) ? dictionaries.subjects : [];
    return list
      .filter(Boolean)
      .map((s) => {
        const id = String(s?.id ?? s?.subject_id ?? s?.value ?? s);
        const label = String(s?.subject ?? s?.label ?? s?.value ?? s);
        return { id, label, raw: s };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [dictionaries?.subjects]);

  const selectedSubjectIds = useMemo(() => {
    return (shot?.subject ?? [])
      .map((s) => s?.subject_id)
      .filter((id) => id != null)
      .map((id) => String(id));
  }, [shot?.subject]);

  const selectedSubjectValues = useMemo(() => {
    const optionById = new Map(subjectOptions.map((o) => [o.id, o]));
    return selectedSubjectIds.map((id) => optionById.get(id) || { id, label: id, raw: null });
  }, [selectedSubjectIds, subjectOptions]);

  const addNewSubject = async (value) => {
    const label = value && value.value ? value.value : value;
    const trimmed = (label || '').trim();
    if (!trimmed) return null;

    const existing = subjectOptions.find(
      (o) => normalizeSubjectKey(o.label) === normalizeSubjectKey(trimmed),
    );
    if (existing) return existing.raw || { id: existing.id, subject: existing.label };

    const newEntry = await dictionaryService.addDictionaryEntry(
      'subjects',
      trimmed,
      'subject',
      scene?.script_id,
    );

    if (newEntry) {
      dictionaries['subjects'].push(newEntry);
      updateDictionaries(dictionaries);
      return newEntry;
    }

    return null;
  };

  // If shot contains IDs not present in dictionary, trigger dict refresh
  useEffect(() => {
    try {
      const dictIds = new Set(subjectOptions.map((o) => o.id));
      const hasMissing = selectedSubjectIds.some((id) => !dictIds.has(id));

      if (hasMissing && typeof setView === 'function') {
        setView((prevView) => ({
          ...prevView,
          updateDictionary: {
            ...(prevView?.updateDictionary || {}),
            subjects: true,
          },
        }));
      }
    } catch (e) {
      // no-op
    }
  }, [selectedSubjectIds, subjectOptions, setView]);

  const showPlaceholder = (selectedSubjectValues?.length || 0) === 0;

  return (
    <Box sx={{ width: '100%' }}>
      <Autocomplete
        multiple
        freeSolo
        disableClearable
        id={id}
        options={subjectOptions}
        value={selectedSubjectValues}
        disabled={locked}
        sx={{
          width: '100%',
          '& .MuiOutlinedInput-root': {
            minHeight: 44,
            alignItems: 'flex-start',
            paddingTop: '6px',
            paddingBottom: '6px',
          },
          '& .MuiAutocomplete-inputRoot': {
            flexWrap: 'wrap',
            alignItems: 'center',
            rowGap: '6px',
            backgroundColor: '#ffffff',
          },
          '& .MuiAutocomplete-input': {
            minWidth: 120,
            flexGrow: 1,
            backgroundColor: '#ffffff',
          },
          '& .MuiAutocomplete-tag': {
            marginTop: 0,
            marginBottom: 0,
            backgroundColor: '#F3E8FA',
          },
          '& .MuiChip-root': {
            height: 26,
            backgroundColor: '#F3E8FA',
          },
          ...(sx || {}),
        }}
        getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt?.label || '')}
        isOptionEqualToValue={(opt, val) => {
          const optId = typeof opt === 'string' ? opt : opt?.id;
          const valId = typeof val === 'string' ? val : val?.id;
          return String(optId) === String(valId);
        }}
        onOpen={() => {
          if (lockKey && typeof lockField === 'function') lockField(lockKey);
        }}
        onClose={() => {
          if (lockKey && typeof unlockField === 'function') unlockField(lockKey);
        }}
        onChange={async (_e, newValue) => {
          const nextIds = [];

          for (const item of newValue || []) {
            if (!item) continue;

            if (typeof item === 'string') {
              const created = await addNewSubject(item);
              if (created) {
                const id = String(created?.id ?? created?.subject_id ?? created?.value);
                nextIds.push(id);
              }
            } else {
              if (item.id != null) nextIds.push(String(item.id));
            }
          }

          const unique = Array.from(new Set(nextIds));
          await handleShotFieldChange?.(shot?.id, 'subject', unique);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Subjects"
            size="small"
            InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
            placeholder={showPlaceholder ? 'Type to add...' : ''}
            onFocus={() => {
              if (lockKey && typeof lockField === 'function') lockField(lockKey);
            }}
            onBlur={() => {
              if (lockKey && typeof unlockField === 'function') unlockField(lockKey);
            }}
          />
        )}
      />
    </Box>
  );
}
